import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { Reveal, Label } from "./anim";
import { PROFILE, PROJECT_TYPES } from "../data/content";
import { Phone, Mail, Linkedin, Globe, ArrowRight, Loader2, Download } from "lucide-react";

const CONTACTS = [
  { icon: Phone, label: "Phone", value: PROFILE.phone, href: `tel:${PROFILE.phone.replace(/[^0-9]/g, "")}`, testid: "contact-phone" },
  { icon: Mail, label: "Email", value: PROFILE.email, href: `mailto:${PROFILE.email}`, testid: "contact-email-link" },
  { icon: Linkedin, label: "LinkedIn", value: "in/acampos", href: PROFILE.linkedin, testid: "contact-linkedin" },
  { icon: Globe, label: "Website", value: PROFILE.websiteLabel, href: PROFILE.website, testid: "contact-website" },
];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", project_type: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. Andres will be in touch shortly.");
      setForm({ name: "", email: "", company: "", project_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please email directly at " + PROFILE.email);
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-[#EFEBDE] border border-[#16281C]/15 px-5 py-4 font-body text-[15px] text-[#16281C] placeholder:text-[#8B978C] rounded-none focus:outline-none focus:border-[#2C5F3A] transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#E7E2D2] py-24 md:py-40 border-t border-[#16281C]/10">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid lg:grid-cols-[1fr_1.1fr] gap-16 lg:gap-24">
        <div>
          <Reveal>
            <Label>Let's Talk</Label>
            <h2 className="mt-6 font-display font-semibold uppercase text-4xl sm:text-5xl lg:text-[5.5vw] tracking-tighter leading-[0.9] text-[#16281C]">
              Start your <span className="text-[#2C5F3A]">project.</span>
            </h2>
            <p className="mt-8 font-body text-base md:text-lg text-[#4C5C50] leading-relaxed max-w-md">
              Available for pre-development consulting, permitting projects, and full-time opportunities. Based in {PROFILE.location} — open to travel and remote work nationwide.
            </p>
            <a
              href={PROFILE.resumeUrl}
              download
              data-testid="contact-resume"
              className="mt-8 inline-flex items-center gap-3 border border-[#16281C]/25 text-[#16281C] font-mono text-xs tracking-[0.2em] uppercase px-7 py-4 hover:border-[#2C5F3A] hover:text-[#2C5F3A] transition-colors"
            >
              <Download size={16} /> Download Résumé
            </a>
          </Reveal>

          <div className="mt-12 space-y-px">
            {CONTACTS.map((c, i) => (
              <Reveal key={c.label} delay={i * 0.06}>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  data-testid={c.testid}
                  className="group flex items-center gap-5 py-5 border-t border-[#16281C]/12 last:border-b hover:pl-3 transition-all duration-300"
                >
                  <c.icon size={20} className="text-[#2C5F3A] shrink-0" />
                  <div className="flex-1">
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8B978C]">{c.label}</p>
                    <p className="font-body text-base md:text-lg text-[#16281C]">{c.value}</p>
                  </div>
                  <ArrowRight size={18} className="text-[#8B978C] group-hover:text-[#2C5F3A] group-hover:translate-x-1 transition-all" />
                </a>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="bg-[#FAF8F1] border border-[#16281C]/12 p-7 md:p-12"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Name *</label>
                <input data-testid="contact-name-input" value={form.name} onChange={update("name")} className={inputCls} placeholder="Jane Developer" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Email *</label>
                <input data-testid="contact-email-input" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="jane@company.com" />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Company</label>
                <input data-testid="contact-company-input" value={form.company} onChange={update("company")} className={inputCls} placeholder="Acme Development" />
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Project Type</label>
                <select data-testid="contact-projecttype-select" value={form.project_type} onChange={update("project_type")} className={`${inputCls} appearance-none cursor-pointer`}>
                  <option value="">Select…</option>
                  {PROJECT_TYPES.map((p) => (
                    <option key={p} value={p} className="bg-[#EFEBDE]">{p}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4">
              <label className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#66766A] block mb-2">Message *</label>
              <textarea data-testid="contact-message-input" value={form.message} onChange={update("message")} rows={5} className={`${inputCls} resize-none`} placeholder="Tell me about your project, timeline, and jurisdictions…" />
            </div>

            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={loading}
              className="mt-6 w-full inline-flex items-center justify-center gap-3 bg-[#2C5F3A] text-[#EFEBDE] font-mono text-xs tracking-[0.2em] uppercase px-8 py-5 hover:bg-[#367A49] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Send Message <ArrowRight size={16} /></>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
